import type { LatLngTuple } from '@groupe-reca/contracts'
import { toLocal, type LatLng } from '@/features/detection/domain/geometry'

// Côté de la rue d'une résidence, relatif au sens d'approche prévu (étape du parcours qui y mène). Jamais déduit de la
// parité du numéro civique. Masqué (`null`) quand la géométrie ne permet pas de trancher : point sur l'axe routier,
// trop loin de la rue, ou projeté au-delà des extrémités de l'étape (coin, cul-de-sac).

export type StreetSide = 'LEFT' | 'RIGHT'

/** Écart minimal à l'axe (m) : en deçà, le point est sur la chaussée et le côté n'est pas fiable. */
export const SIDE_MIN_OFFSET_M = 4
/** Écart maximal (m) : au-delà, la résidence n'est pas sur cette rue. */
export const SIDE_MAX_OFFSET_M = 60
/** Seule la fin de l'étape compte (m avant l'arrivée) : c'est l'approche de la résidence. */
export const SIDE_APPROACH_WINDOW_M = 150

/**
 * @param approach géométrie de l'étape qui arrive à la résidence (sens de parcours prévu)
 * @param residence position d'intervention (pas le drapeau, souvent posé sur la rue)
 * @param reversed le tracteur parcourt réellement cette approche à rebours (sens confirmé sur ≥ 15 m)
 */
export function streetSide(approach: readonly LatLngTuple[], residence: LatLng, reversed = false): StreetSide | null {
  const last = approach[approach.length - 1]
  if (!last || approach.length < 2) return null
  const origin = { lat: last[0], lng: last[1] }
  const points = approach.map(([lat, lng]) => toLocal(origin, { lat, lng }))
  const target = toLocal(origin, residence)
  let remaining = 0
  let best: { distance: number; cross: number; interior: boolean } | null = null
  for (let index = points.length - 1; index > 0 && remaining <= SIDE_APPROACH_WINDOW_M; index -= 1) {
    const a = points[index - 1]
    const b = points[index]
    if (!a || !b) continue
    const dx = b.x - a.x
    const dy = b.y - a.y
    const length = Math.hypot(dx, dy)
    remaining += length
    if (length < 0.5) continue
    const t = ((target.x - a.x) * dx + (target.y - a.y) * dy) / (length * length)
    const clamped = Math.max(0, Math.min(1, t))
    const px = a.x + dx * clamped
    const py = a.y + dy * clamped
    const distance = Math.hypot(target.x - px, target.y - py)
    if (!best || distance < best.distance) {
      best = { distance, cross: dx * (target.y - a.y) - dy * (target.x - a.x), interior: t > -0.05 && t < 1.05 }
    }
  }
  if (!best || !best.interior || best.distance < SIDE_MIN_OFFSET_M || best.distance > SIDE_MAX_OFFSET_M) return null
  // Repère est / nord : produit vectoriel positif = à gauche du sens de marche.
  const side: StreetSide = best.cross > 0 ? 'LEFT' : 'RIGHT'
  if (!reversed) return side
  return side === 'LEFT' ? 'RIGHT' : 'LEFT'
}
